/**
 * Marker Selection Plugin for Search Results Map Variant
 *
 * Applies and removes a selected visual state on the DOM marker element that
 * corresponds to the currently selected marker in Redux state.
 *
 * Single responsibility: keep the `nhs-c-marker--selected` class in sync with
 * `selectedMarker` — adding it when a marker is selected, removing it when the
 * selection is cleared.
 *
 * This plugin does NOT:
 * - Open the carousel or render cards (see carousel.plugin)
 * - Handle hover state from list-view focus (see markerHighlight.plugin)
 * - Dispatch marker selection or navigate URLs (see markers.plugin)
 *
 * Reason to change: the selected-marker CSS class name, or the DOM attribute
 * strategy used to locate marker elements.
 *
 * @module searchResultsMapVariant/plugins/markerSelection.plugin
 */

import { distinctUntilChanged, map } from 'rxjs';
import { selectSelectedMarker } from '../redux/selectors';
import findDomMarkerElement from '../components/markersDom';

const SELECTED_CLASS = 'nhs-c-marker--hovered';
const ACTIVE_CLASS = 'active';

/**
 * Creates the markerSelection plugin.
 *
 * @returns {{ name: string, init: Function, start: Function, stop: Function }}
 */
export default function createMarkerSelectionPlugin() {
  const pluginState = {
    started: false,
    mapContainer: null,
    store$: null,
    selectedMarkerElement: null,
    subscriptions: [],
  };

  function applySelectionStyles(selectedMarker) {
    const markerElement = findDomMarkerElement(selectedMarker, pluginState.mapContainer);
    if (!markerElement) {
      return;
    }

    markerElement.classList.add(SELECTED_CLASS);
    markerElement.parentElement?.classList.add(ACTIVE_CLASS);
    pluginState.selectedMarkerElement = markerElement;
  }

  function removeSelectionStyles() {
    if (pluginState.selectedMarkerElement) {
      pluginState.selectedMarkerElement.classList.remove(SELECTED_CLASS);
      pluginState.selectedMarkerElement.parentElement?.classList.remove(ACTIVE_CLASS);
      pluginState.selectedMarkerElement = null;
    }
  }

  function handleSelectedMarkerChange(selectedMarker) {
    removeSelectionStyles();

    if (selectedMarker) {
      applySelectionStyles(selectedMarker);
    }
  }

  function setupSubscriptions() {
    pluginState.subscriptions.push(
      pluginState.store$.pipe(
        map(selectSelectedMarker),
        distinctUntilChanged(),
      ).subscribe(handleSelectedMarkerChange),
    );
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    pluginState.subscriptions = [];
  }

  return {
    name: 'marker-selection',

    async init(context) {
      context.state.markerSelection = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.mapContainer = context.root;
      pluginState.store$ = context.services.store$;

      if (!pluginState.mapContainer) {
        return;
      }

      setupSubscriptions();

      pluginState.started = true;
      context.state.markerSelection = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      removeSelectionStyles();

      pluginState.mapContainer = null;
      pluginState.store$ = null;
      pluginState.started = false;

      delete context.state.markerSelection;
    },
  };
}
